import * as THREE from 'three';
import {
  applyInertia,
  clampOrbit,
  poseToSpherical,
  shortestAngleDelta,
  sphericalToPose,
  type InertiaState,
  type OrbitBounds,
  type Pose,
  type Spherical,
  type Vec3
} from './orbit';
import { easeInOutCubic } from '../utils/easing';

export type CameraMode =
  | { kind: 'idle' }
  | {
      /**
       * Spherical-arc transition between two orbital frames. Both endpoints
       * are expressed as a target + spherical (az, polar, radius) pair so we
       * can interpolate around the scene rather than through it. A `bumpFactor`
       * adds a sine-shaped radius bulge (zoom out + back in) over the flight.
       */
      kind: 'animating';
      fromTarget: Vec3;
      fromSpherical: Spherical;
      toTarget: Vec3;
      toSpherical: Spherical;
      startedAt: number;
      durationMs: number;
      bumpFactor: number;
    }
  | { kind: 'orbit'; target: Vec3; bounds: OrbitBounds };

export interface DoubleClickEvent {
  worldPoint: Vec3;
}

export interface CameraController {
  setMode(mode: CameraMode): void;
  getMode(): CameraMode;
  tick(now: number): void;
  getCurrentPose(): Pose;
  attachInput(
    canvas: HTMLCanvasElement,
    scene: THREE.Scene,
    options?: { splatRaycast?: (ndcX: number, ndcY: number) => THREE.Vector3 | null }
  ): void;
  onDoubleClick(handler: (e: DoubleClickEvent) => void): void;
  onAnimationDone(handler: () => void): void;
  dispose(): void;
}

const SENSITIVITY = 0.005;
const WHEEL_SENSITIVITY = 0.001;
const DAMPING = 0.92;
const TARGET_TRANSITION_MS = 600;

interface TargetTransition {
  from: Vec3;
  to: Vec3;
  startedAt: number;
  durationMs: number;
  cameraPosFrozen: [number, number, number];
}

function isPinObject(obj: THREE.Object3D): boolean {
  let o: THREE.Object3D | null = obj;
  while (o) {
    if (o.userData && o.userData.isPin) return true;
    o = o.parent;
  }
  return false;
}

export function createCameraController(camera: THREE.PerspectiveCamera): CameraController {
  let mode: CameraMode = { kind: 'idle' };
  let lastTickNow = performance.now();
  let dragging = false;
  let lastPointer: { x: number; y: number; t: number } | null = null;
  const pointerSamples: Array<{ t: number; dAz: number; dPo: number }> = [];

  let internal: InertiaState = {
    azimuth: 0,
    polar: Math.PI / 2,
    radius: 50,
    vAz: 0,
    vPo: 0,
    vR: 0
  };
  let targetTransition: TargetTransition | null = null;

  let doubleClickHandler: (e: DoubleClickEvent) => void = () => {};
  let animationDoneHandler: () => void = () => {};
  let attachedScene: THREE.Scene | null = null;
  const raycaster = new THREE.Raycaster();
  const ndc = new THREE.Vector2();

  function applyPose(pose: Pose) {
    camera.position.set(pose.position[0], pose.position[1], pose.position[2]);
    camera.lookAt(pose.target[0], pose.target[1], pose.target[2]);
    if (pose.fov !== undefined && Math.abs(camera.fov - pose.fov) > 1e-3) {
      camera.fov = pose.fov;
      camera.updateProjectionMatrix();
    }
  }

  function getCurrentPose(): Pose {
    const fwd = new THREE.Vector3();
    camera.getWorldDirection(fwd);
    const pos = camera.position;
    const r = mode.kind === 'orbit' ? Math.max(internal.radius, 0.1) : 50;
    const tgt = pos.clone().add(fwd.multiplyScalar(r));
    return {
      position: [pos.x, pos.y, pos.z],
      target: [tgt.x, tgt.y, tgt.z],
      fov: camera.fov
    };
  }

  function syncSphericalFromCamera(target: Vec3, bounds: OrbitBounds) {
    const pose: Pose = {
      position: [camera.position.x, camera.position.y, camera.position.z],
      target
    };
    // Clamp immediately on assignment rather than relying on the next tick.
    // Same-tick reads of `internal` (e.g. getCurrentPose) would otherwise see
    // out-of-bounds values during the one-frame window before tick clamps.
    const sph = clampOrbit(poseToSpherical(pose, target), bounds);
    internal.azimuth = sph.azimuth;
    internal.polar = sph.polar;
    internal.radius = sph.radius;
    internal.vAz = 0;
    internal.vPo = 0;
    internal.vR = 0;
  }

  function setMode(next: CameraMode) {
    // Smoothly transition the orbit target if we're already in orbit mode and
    // the target moved (e.g. double-click drift). The camera position freezes
    // and we lerp the lookAt target over TARGET_TRANSITION_MS, then resync the
    // spherical state to the final target.
    if (mode.kind === 'orbit' && next.kind === 'orbit') {
      const old = mode.target;
      const nu = next.target;
      const targetMoved = old[0] !== nu[0] || old[1] !== nu[1] || old[2] !== nu[2];
      mode = next;
      if (targetMoved) {
        targetTransition = {
          from: [old[0], old[1], old[2]],
          to: [nu[0], nu[1], nu[2]],
          startedAt: performance.now(),
          durationMs: TARGET_TRANSITION_MS,
          cameraPosFrozen: [camera.position.x, camera.position.y, camera.position.z]
        };
        // Drop any inertia so the gaze swing isn't fighting residual drag.
        internal.vAz = 0;
        internal.vPo = 0;
        internal.vR = 0;
      }
      return;
    }

    targetTransition = null;
    mode = next;
    if (next.kind === 'orbit') {
      syncSphericalFromCamera(next.target, next.bounds);
    }
  }

  function tick(now: number) {
    const dt = Math.max(0, Math.min(0.1, (now - lastTickNow) / 1000));
    lastTickNow = now;

    switch (mode.kind) {
      case 'idle':
        return;
      case 'animating': {
        const t = Math.max(0, Math.min(1, (now - mode.startedAt) / mode.durationMs));
        const eased = easeInOutCubic(t);

        // Lerp the target linearly — straightforward, since target is a single
        // point in scene space and rarely needs an arc.
        const tx =
          mode.fromTarget[0] + (mode.toTarget[0] - mode.fromTarget[0]) * eased;
        const ty =
          mode.fromTarget[1] + (mode.toTarget[1] - mode.fromTarget[1]) * eased;
        const tz =
          mode.fromTarget[2] + (mode.toTarget[2] - mode.fromTarget[2]) * eased;

        // Lerp spherical: azimuth via shortest signed delta (avoids wrong-way
        // wrap-around), polar and radius linearly.
        const dAz = shortestAngleDelta(mode.fromSpherical.azimuth, mode.toSpherical.azimuth);
        const az = mode.fromSpherical.azimuth + dAz * eased;
        const po =
          mode.fromSpherical.polar +
          (mode.toSpherical.polar - mode.fromSpherical.polar) * eased;
        const baseR =
          mode.fromSpherical.radius +
          (mode.toSpherical.radius - mode.fromSpherical.radius) * eased;

        // Radius bump: sin²(πt) bulge — zero value AND zero slope at both
        // endpoints, peaks at midpoint. The squared shape gives a soft onset
        // and (more importantly) a soft offset, so the bump doesn't taper
        // abruptly back into baseR right before arrival.
        const bumpScale = Math.max(mode.fromSpherical.radius, mode.toSpherical.radius);
        const sinT = Math.sin(t * Math.PI);
        const r = baseR + bumpScale * mode.bumpFactor * sinT * sinT;

        const pose = sphericalToPose([tx, ty, tz], { azimuth: az, polar: po, radius: r });
        applyPose(pose);
        if (t >= 1) {
          mode = { kind: 'idle' };
          animationDoneHandler();
        }
        return;
      }
      case 'orbit': {
        if (targetTransition) {
          const t = Math.max(
            0,
            Math.min(1, (now - targetTransition.startedAt) / targetTransition.durationMs)
          );
          const e = easeInOutCubic(t);
          const tx =
            targetTransition.from[0] + (targetTransition.to[0] - targetTransition.from[0]) * e;
          const ty =
            targetTransition.from[1] + (targetTransition.to[1] - targetTransition.from[1]) * e;
          const tz =
            targetTransition.from[2] + (targetTransition.to[2] - targetTransition.from[2]) * e;
          // Freeze camera position; smoothly swing the gaze toward the new target.
          camera.position.set(
            targetTransition.cameraPosFrozen[0],
            targetTransition.cameraPosFrozen[1],
            targetTransition.cameraPosFrozen[2]
          );
          camera.lookAt(tx, ty, tz);
          if (t >= 1) {
            syncSphericalFromCamera(mode.target, mode.bounds);
            targetTransition = null;
          }
          return;
        }
        if (!dragging) {
          internal = applyInertia(internal, dt, DAMPING);
        }
        const clamped = clampOrbit(
          { azimuth: internal.azimuth, polar: internal.polar, radius: internal.radius },
          mode.bounds
        );
        internal.azimuth = clamped.azimuth;
        internal.polar = clamped.polar;
        internal.radius = clamped.radius;
        const pose = sphericalToPose(mode.target, {
          azimuth: internal.azimuth,
          polar: internal.polar,
          radius: internal.radius
        });
        applyPose(pose);
        return;
      }
    }
  }

  let detachInput: () => void = () => {};

  function attachInput(
    canvas: HTMLCanvasElement,
    scene: THREE.Scene,
    options: { splatRaycast?: (ndcX: number, ndcY: number) => THREE.Vector3 | null } = {}
  ) {
    attachedScene = scene;
    const splatRaycast = options.splatRaycast;

    // Multi-touch pinch state: track all active pointers, compute distance
    // between the first two when active, scale radius by the ratio.
    const activePointers = new Map<number, { x: number; y: number }>();
    let pinching = false;
    let pinchInitialDistance = 0;
    let pinchInitialRadius = 0;

    function pinchDistance(): number {
      const ps = Array.from(activePointers.values());
      if (ps.length < 2) return 0;
      return Math.hypot(ps[0]!.x - ps[1]!.x, ps[0]!.y - ps[1]!.y);
    }

    const onPointerDown = (e: PointerEvent) => {
      if (mode.kind !== 'orbit') return;
      if (targetTransition) return;
      activePointers.set(e.pointerId, { x: e.clientX, y: e.clientY });

      if (activePointers.size === 1) {
        // Single pointer = orbit drag.
        dragging = true;
        lastPointer = { x: e.clientX, y: e.clientY, t: performance.now() };
        pointerSamples.length = 0;
        canvas.setPointerCapture(e.pointerId);
      } else if (activePointers.size === 2) {
        // Second finger landed: switch from drag to pinch. Cancel orbit
        // velocity so when pinch ends and one finger lifts, the user
        // doesn't get a surprise inertial spin.
        dragging = false;
        internal.vAz = 0;
        internal.vPo = 0;
        pointerSamples.length = 0;
        pinching = true;
        pinchInitialDistance = pinchDistance();
        pinchInitialRadius = internal.radius;
      }
    };

    const onPointerMove = (e: PointerEvent) => {
      if (!activePointers.has(e.pointerId)) return;
      activePointers.set(e.pointerId, { x: e.clientX, y: e.clientY });

      if (pinching && activePointers.size >= 2) {
        const d = pinchDistance();
        if (d > 0 && pinchInitialDistance > 0) {
          // Spread fingers (d > initial) → radius shrinks (zoom in).
          internal.radius = pinchInitialRadius * (pinchInitialDistance / d);
        }
        return;
      }

      if (!dragging || mode.kind !== 'orbit' || !lastPointer) return;
      const dx = e.clientX - lastPointer.x;
      const dy = e.clientY - lastPointer.y;
      const dAz = dx * SENSITIVITY;
      const dPo = -dy * SENSITIVITY;
      internal.azimuth += dAz;
      internal.polar += dPo;
      const t = performance.now();
      pointerSamples.push({ t, dAz, dPo });
      while (pointerSamples.length > 0 && t - pointerSamples[0]!.t > 100) {
        pointerSamples.shift();
      }
      lastPointer = { x: e.clientX, y: e.clientY, t };
    };

    const onPointerUp = (e: PointerEvent) => {
      activePointers.delete(e.pointerId);
      try {
        canvas.releasePointerCapture(e.pointerId);
      } catch {
        // pointer was already released
      }

      if (activePointers.size < 2) {
        pinching = false;
      }
      if (activePointers.size === 0) {
        // All fingers up: end the orbit drag, capture velocity for inertia.
        if (dragging && pointerSamples.length >= 2) {
          const first = pointerSamples[0]!;
          const last = pointerSamples[pointerSamples.length - 1]!;
          const dt = Math.max(0.016, (last.t - first.t) / 1000);
          let sumAz = 0;
          let sumPo = 0;
          for (const s of pointerSamples) {
            sumAz += s.dAz;
            sumPo += s.dPo;
          }
          internal.vAz = sumAz / dt;
          internal.vPo = sumPo / dt;
        }
        dragging = false;
        pointerSamples.length = 0;
        lastPointer = null;
      } else if (activePointers.size === 1) {
        // Coming out of pinch (one finger remaining): re-arm drag from the
        // remaining pointer's current position so we don't jump.
        const remaining = Array.from(activePointers.values())[0]!;
        dragging = true;
        lastPointer = { x: remaining.x, y: remaining.y, t: performance.now() };
        pointerSamples.length = 0;
      }
    };

    const onWheel = (e: WheelEvent) => {
      if (mode.kind !== 'orbit') return;
      e.preventDefault();
      internal.radius *= 1 + e.deltaY * WHEEL_SENSITIVITY;
    };

    const onDblClick = (e: MouseEvent) => {
      if (!attachedScene) return;
      const rect = canvas.getBoundingClientRect();
      ndc.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
      ndc.y = -(((e.clientY - rect.top) / rect.height) * 2 - 1);

      // Prefer a splat-aware raycast when available: mkkellogg's SplatMesh
      // geometry is an instanced unit quad — THREE.Raycaster.intersectObjects
      // would either miss it or return the wrong point. The splatRaycast
      // callback wraps mkkellogg's internal Raycaster which traverses the
      // splat octree and returns the actual world-space splat surface hit.
      if (splatRaycast) {
        const p = splatRaycast(ndc.x, ndc.y);
        if (p) {
          doubleClickHandler({ worldPoint: [p.x, p.y, p.z] });
          return;
        }
      }

      // Fallback path for non-splat objects (older Spark setup used this for
      // the splat too because Spark's mesh was THREE-raycastable; kept for
      // future objects we might add).
      raycaster.setFromCamera(ndc, camera);
      const hits = raycaster.intersectObjects(attachedScene.children, true);
      const realHit = hits.find((h) => !isPinObject(h.object));
      if (realHit) {
        const p = realHit.point;
        doubleClickHandler({ worldPoint: [p.x, p.y, p.z] });
      }
    };

    canvas.addEventListener('pointerdown', onPointerDown);
    canvas.addEventListener('pointermove', onPointerMove);
    canvas.addEventListener('pointerup', onPointerUp);
    canvas.addEventListener('pointercancel', onPointerUp);
    canvas.addEventListener('wheel', onWheel, { passive: false });
    canvas.addEventListener('dblclick', onDblClick);

    detachInput = () => {
      canvas.removeEventListener('pointerdown', onPointerDown);
      canvas.removeEventListener('pointermove', onPointerMove);
      canvas.removeEventListener('pointerup', onPointerUp);
      canvas.removeEventListener('pointercancel', onPointerUp);
      canvas.removeEventListener('wheel', onWheel);
      canvas.removeEventListener('dblclick', onDblClick);
    };
  }

  return {
    setMode,
    getMode: () => mode,
    tick,
    getCurrentPose,
    attachInput,
    onDoubleClick: (h) => {
      doubleClickHandler = h;
    },
    onAnimationDone: (h) => {
      animationDoneHandler = h;
    },
    dispose: () => detachInput()
  };
}
